/**
 * User-scheduled one-shot entity reminders — the frontend seam.
 *
 * A thin naming + invalidation layer over the ORVAL-generated `/api/v1/reminders/entity`
 * hooks. Generated, deliberately: both sides come from one OpenAPI document, so this
 * cannot drift the way the hand-rolled `useEntityFollow` sibling can
 * (`.claude/rules/rules-frontend.md` § hand-rolled API calls). What is added here is the
 * one thing the generated client cannot know — that the two READ surfaces are two views
 * of the same rows, so every mutation must invalidate BOTH:
 *
 *   - `GET /entity/{entity_type}/{entity_id}` — the caller's own pending reminders on one
 *     entity, rendered inside `RemindMeButton`'s popover.
 *   - `GET /entity/upcoming` — the caller's pending reminders as a RECIPIENT, rendered in
 *     the notifications bell. A reminder someone else set for you appears only here.
 *
 * Cancelling from the bell has to clear the popover's list on the entity page behind it,
 * and creating from the popover has to appear in the bell — which is why the invalidation
 * is whole-family (the `/api/v1/reminders/entity` prefix) rather than per-key.
 *
 * Contract: `docs/platform/entity-reminders-design.md`.
 */
import { useQueryClient } from '@tanstack/react-query';

import {
  getListEntityRemindersApiV1RemindersEntityEntityTypeEntityIdGetQueryKey,
  getListUpcomingRemindersApiV1RemindersEntityUpcomingGetQueryKey,
  useCancelEntityReminderApiV1RemindersEntityReminderIdDelete,
  useCreateEntityReminderApiV1RemindersEntityPost,
  useListEntityRemindersApiV1RemindersEntityEntityTypeEntityIdGet,
  useListUpcomingRemindersApiV1RemindersEntityUpcomingGet,
  useUpdateEntityReminderApiV1RemindersEntityReminderIdPatch,
} from '@/generated/api/reminders/reminders';
import type { EntityReminder } from '@/generated/api/models/entityReminder';

export type { EntityReminder };

/** How many upcoming reminders the bell's section asks for. */
export const UPCOMING_REMINDERS_LIMIT = 5;

/**
 * The URL prefix every reminder query key starts with.
 *
 * It is also what lets the freshness layer resolve the owning module off the key
 * (`reminders` → `shared` in `API_PREFIX_MODULES`). It is deliberately one segment
 * narrower than the route family — `/api/v1/reminders` also hosts the nagging
 * framework's settings endpoints, which share none of this data.
 */
const REMINDER_URL_PREFIX = '/api/v1/reminders/entity';

/**
 * Match every reminder cache entry by STRING prefix, not by query-key prefix.
 *
 * `invalidateQueries({ queryKey: ['/api/v1/reminders/entity'] })` looks like it would do
 * this and does nothing at all: React Query matches a key prefix ELEMENT BY ELEMENT, and
 * orval bakes the path parameters into the first element — the per-entity list's whole key
 * is `['/api/v1/reminders/entity/deal/<uuid>']`, a single string that is never equal to
 * the shorter one. The invalidation therefore succeeded silently and refreshed nothing:
 * creating a reminder toasted "Reminder set" while the popover's own list below it went on
 * saying "You have no reminders on this record", for as long as the popover stayed open.
 */
function isReminderQuery(queryKey: readonly unknown[]): boolean {
  return typeof queryKey[0] === 'string' && queryKey[0].startsWith(REMINDER_URL_PREFIX);
}

/** The caller's own pending reminders on one entity — the popover's list. */
export function useEntityReminders({
  entityType,
  entityId,
  enabled = true,
}: {
  entityType: string;
  entityId: string;
  enabled?: boolean;
}) {
  return useListEntityRemindersApiV1RemindersEntityEntityTypeEntityIdGet(
    entityType,
    entityId,
    { query: { enabled: enabled && !!entityType && !!entityId } },
  );
}

/** The caller's pending reminders as a recipient — the bell's section. */
export function useUpcomingReminders({ enabled = true }: { enabled?: boolean } = {}) {
  return useListUpcomingRemindersApiV1RemindersEntityUpcomingGet(
    { limit: UPCOMING_REMINDERS_LIMIT },
    { query: { enabled } },
  );
}

/**
 * Invalidate every reminder read surface.
 *
 * Exported so a caller holding its own `queryClient` (the bell, which cancels a row it did
 * not create) settles the same way a mutation from this module does.
 */
export function useInvalidateReminders() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ predicate: (query) => isReminderQuery(query.queryKey) });
  };
}

export function useCreateEntityReminder() {
  const invalidate = useInvalidateReminders();
  return useCreateEntityReminderApiV1RemindersEntityPost({
    mutation: { onSuccess: invalidate },
  });
}

export function useUpdateEntityReminder() {
  const invalidate = useInvalidateReminders();
  return useUpdateEntityReminderApiV1RemindersEntityReminderIdPatch({
    mutation: { onSuccess: invalidate },
  });
}

export function useCancelEntityReminder() {
  const invalidate = useInvalidateReminders();
  return useCancelEntityReminderApiV1RemindersEntityReminderIdDelete({
    mutation: { onSuccess: invalidate },
  });
}

/** The query keys the two read surfaces use — for tests and for targeted seeding. */
export const entityReminderKeys = {
  forEntity: (entityType: string, entityId: string) =>
    getListEntityRemindersApiV1RemindersEntityEntityTypeEntityIdGetQueryKey(
      entityType,
      entityId,
    ),
  upcoming: () =>
    getListUpcomingRemindersApiV1RemindersEntityUpcomingGetQueryKey({
      limit: UPCOMING_REMINDERS_LIMIT,
    }),
};
